import { useToast } from "@app/components/toast";
import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import type { unprojectedDecisions_RetireMutation } from "@app/queries/unprojectedDecisions_RetireMutation.graphql";

export type UnprojectedDecision = {
  readonly id: string;
  readonly dimension: string;
  readonly valueLabel: string | null | undefined;
  readonly txnDescription: string | null | undefined;
  readonly txnPostingDate: string | null | undefined;
  readonly txnAmount: number | null | undefined;
  readonly decidedBy: string | null | undefined;
};

const retireMutation = graphql`
  mutation unprojectedDecisions_RetireMutation(
    $input: RetireTxnDecisionInputObject!
  ) {
    retireTxnDecision(input: $input) {
      retiredCount
      errors {
        message
      }
    }
  }
`;

// What the decision was, in the language of the thing it decided.
const DIMENSION_LABEL: Record<string, string> = {
  ATTRIBUTION: "Counterparty",
  CLASSIFICATION: "Classification",
  NATURE: "Expense nature",
  NEEDS_REVIEW: "Sent to review",
  INTERNAL_LINK: "Internal transfer",
};

function money(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "";
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

/**
 * Decisions someone made by hand that no longer land on any transaction — the
 * QuickBooks record was deleted, or a re-issued statement changed the row they were
 * anchored to.
 *
 * The whole point is that this is VISIBLE. Before, a hand-made categorization on a
 * QuickBooks transaction simply reverted on the next sync with nothing logged; the
 * surfaces that would have shown it couldn't see those rows at all. So this section
 * says what was decided, what the transaction looked like at the time, and who
 * decided it — enough to re-decide on the replacement row or dismiss it deliberately.
 *
 * Hidden entirely when the set is empty: a permanently-visible "0 problems" panel
 * trains people to stop reading it.
 */
export default function UnprojectedDecisions({
  decisions,
  onChanged,
}: {
  decisions: readonly UnprojectedDecision[];
  onChanged: () => void;
}) {
  const notify = useToast();
  const [retiring, setRetiring] = useState<string | null>(null);

  if (decisions.length === 0) return null;

  const dismiss = (id: string) => {
    setRetiring(id);
    commitMutation<unprojectedDecisions_RetireMutation>(environment, {
      mutation: retireMutation,
      variables: { input: { decisionIds: [id] } },
      onCompleted: (response) => {
        setRetiring(null);
        const errors = response.retireTxnDecision?.errors ?? [];
        if (errors.length > 0) {
          notify({ message: errors[0].message, kind: "error" });
          return;
        }
        onChanged();
      },
      onError: () => {
        setRetiring(null);
        notify({ message: "Could not dismiss that decision.", kind: "error" });
      },
    });
  };

  return (
    <div className="bg-white border border-amber-300 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-amber-200 bg-amber-50">
        <h2 className="text-sm font-semibold text-gray-900">
          {decisions.length} categorization
          {decisions.length === 1 ? "" : "s"} you made no longer{" "}
          {decisions.length === 1 ? "matches a" : "match any"} transaction
        </h2>
        <p className="text-xs text-gray-600 mt-1 max-w-2xl">
          The transaction each of these described was deleted in QuickBooks, or
          changed enough that it no longer matches. Nothing was applied silently —
          re-decide on the replacement transaction, or dismiss it here.
        </p>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left font-medium px-4 py-2">Transaction</th>
            <th className="text-left font-medium px-4 py-2">You decided</th>
            <th className="text-right font-medium px-4 py-2">Amount</th>
            <th className="text-left font-medium px-4 py-2">By</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {decisions.map((decision) => (
            <tr key={decision.id} className="border-t border-gray-100">
              <td className="px-4 py-2">
                <div className="text-gray-900">
                  {decision.txnDescription ?? "Transaction no longer available"}
                </div>
                <div className="text-xs text-gray-500">
                  {decision.txnPostingDate ?? ""}
                </div>
              </td>
              <td className="px-4 py-2">
                <span className="text-gray-500">
                  {DIMENSION_LABEL[decision.dimension] ?? decision.dimension}
                </span>
                {decision.valueLabel ? (
                  <span className="text-gray-900"> → {decision.valueLabel}</span>
                ) : null}
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                {money(decision.txnAmount)}
              </td>
              <td className="px-4 py-2 text-gray-500">
                {decision.decidedBy ?? ""}
              </td>
              <td className="px-4 py-2 text-right">
                <button
                  type="button"
                  onClick={() => dismiss(decision.id)}
                  disabled={retiring === decision.id}
                  className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  {retiring === decision.id ? "Dismissing…" : "Dismiss"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
